<script setup>
/**
 * More about {topic}: up to three other lessons as mini cards on the paper,
 * under a hairline (lesson-redo DESIGN.md K1).
 *
 * Wide: three in a row, each the picture 120px tall (or a mini fan of three
 * cards on plum), *Someone asked:* "…" (2 lines), the lesson in italic (2
 * lines). Phone: rows with a 72px square thumbnail, the lead-in on one line,
 * the lesson on two, and a ›. A tap emits `open` (the page loads it in the
 * same window).
 */
import { computed } from 'vue'
import { leadIn, lessonCards, moreAbout } from '@/utils/lesson'

const props = defineProps({
  lesson: { type: Object, required: true },
  items: { type: Array, default: () => [] },
})
const emit = defineEmits(['open'])

const heading = computed(() => moreAbout(props.lesson))
const list = computed(() => (props.items || [])
  .filter((x) => x && x.short_uuid && x.short_uuid !== (props.lesson && props.lesson.short_uuid))
  .slice(0, 3)
  .map((x) => ({
    item: x,
    pic: x.image_url && !x.cards_lead ? x.image_url : '',
    fan: lessonCards(x).filter((c) => c.image_url).slice(0, 3),
    lead: leadIn(x),
  })))
</script>

<template>
  <nav v-if="list.length" class="rl" data-testid="lesson-more" :aria-label="heading">
    <p class="rl-h">{{ heading }}</p>
    <ul class="rl-list">
      <li v-for="r in list" :key="r.item.short_uuid">
        <button type="button" class="rl-c" data-testid="lesson-related" @click="emit('open', r.item)">
          <span class="rl-th" :class="{ 'fan-th': !r.pic }" aria-hidden="true">
            <img v-if="r.pic" :src="r.pic" alt="" />
            <template v-else>
              <span v-for="(c, k) in r.fan" :key="k" :style="{ '--k': k - 1 }"><img :src="c.image_url" alt="" :class="{ rev: c.reversed }" /></span>
            </template>
          </span>
          <span class="rl-t">
            <span class="rl-li"><i v-if="r.lead.key" class="li-k">{{ r.lead.key }}</i>{{ r.lead.key ? ' ' : '' }}{{ r.lead.text }}</span>
            <span class="rl-ls">{{ r.item.lesson }}</span>
          </span>
          <span class="rl-go" aria-hidden="true">›</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.rl {
  --gold: #c9a94e; --golddk: #8a6a2a; --ink: #2a1845; --ink2: #3d2f55; --muted: #7a6a8f; --plum: #2a1845;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  margin: 0 22px; padding: 20px 0 30px; border-top: 1px solid rgba(201, 169, 78, .35); text-align: left;
}
.rl *, .rl *::before, .rl *::after { box-sizing: border-box; }
.rl img { display: block; }
.rl-h { margin: 0 0 12px; font: 600 10.5px var(--display); letter-spacing: .2em; text-transform: uppercase; color: var(--golddk); }
.rl-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
.rl-c {
  display: grid; grid-template-columns: 72px minmax(0, 1fr) 16px; gap: 14px; align-items: center; width: 100%; padding: 8px 12px 8px 8px; border-radius: 14px;
  cursor: pointer; text-align: left; font: inherit; border: 1px solid rgba(201, 169, 78, .35); background: #fbf6ec; color: var(--ink);
  transition: border-color .2s, transform .2s, box-shadow .2s;
}
.rl-c:hover { border-color: rgba(201, 169, 78, .8); transform: translateY(-1px); box-shadow: 0 12px 22px -16px rgba(42, 24, 69, .6); }
.rl-c:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.rl-th { position: relative; width: 72px; height: 72px; border-radius: 10px; overflow: hidden; background: var(--plum); }
.rl-th > img { width: 100%; height: 100%; object-fit: cover; }
.rl-th.fan-th { background: radial-gradient(70% 90% at 50% 55%, #4b2a7a, #160a2b); }
.rl-th.fan-th > span { position: absolute; left: 50%; top: 50%; width: 24px; height: 36px; margin: -18px 0 0 -12px; border-radius: 3px; overflow: hidden;
  border: 1px solid rgba(231, 201, 131, .8); transform: translateX(calc(var(--k) * 14px)) rotate(calc(var(--k) * 9deg)); }
.rl-th.fan-th > span img { width: 100%; height: 100%; object-fit: cover; }
.rl-th img.rev { transform: rotate(180deg); }
.rl-t { min-width: 0; }
.rl-li { display: block; font: 500 13.5px/1.35 var(--serif); color: var(--ink2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.li-k { font-style: italic; color: var(--muted); margin-right: .2em; }
.rl-ls { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; overflow: hidden; margin-top: 2px;
  font: italic 600 18px/1.2 var(--serif); color: var(--ink); overflow-wrap: anywhere; }
.rl-go { color: var(--gold); font: 400 20px Inter, system-ui, sans-serif; }
@container (min-width: 760px) {
  .rl { margin: 0 72px; }
  .rl-list { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
  .rl-c { grid-template-columns: 1fr; padding: 0; overflow: hidden; align-items: start; height: 100%; }
  .rl-th { width: 100%; height: 120px; border-radius: 0; }
  .rl-th.fan-th > span { width: 40px; height: 60px; margin: -30px 0 0 -20px; transform: translateX(calc(var(--k) * 24px)) rotate(calc(var(--k) * 9deg)); }
  .rl-t { padding: 0 14px 14px; }
  .rl-li { white-space: normal; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2; }
  .rl-go { display: none; }
}
@media (prefers-reduced-motion: reduce) { .rl-c { transition: none; } .rl-c:hover { transform: none; } }
</style>
